import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Der Inhalt der Seite zu "Fotobox mit Sofortdruck".
 *
 * Nach der Startseite die zweitstärkste Seite der Domain: 13.613
 * Impressionen in 90 Tagen, Position 15,3, 169 Klicks. Sie lief über
 * dieselbe Vorlage wie 190 Stadtseiten und beantwortete keine einzige der
 * Fragen, die zu diesem Begriff gestellt werden — was Drucken kostet, wie
 * lange es dauert, wie viele Bilder herauskommen.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function SofortdruckInhalt({ analyse, stand }) {
  if (!analyse) return null;

  const { flatrate, kontingent, sekunden } = analyse;
  // Bei 12 Sekunden je Bild und drei Bildern je Durchgang.
  const proStunde = sekunden ? Math.floor(3600 / (sekunden.median * 3)) : null;

  return (
    <div className="space-y-12 lg:space-y-16">
      <Abschnitt
        id="was-kostet-druck"
        titel="Was der Drucker kostet"
        untertitel="Der Unterschied zwischen einem digitalen und einem druckenden Paket — in Zahlen."
      >
        <p>
          {analyse.anzahlBoxen} der {analyse.anzahlBoxenGesamt} Fotoboxen im
          Vergleich drucken vor Ort. Über alle {analyse.anzahlPakete}{" "}
          Druckpakete beginnt der Preis bei{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.min)} €
          </strong>
          , die Hälfte liegt unter {euro(analyse.medianMitDruck)} €.
        </p>
        {analyse.aufschlag !== null && (
          <p className="mt-4">
            Rein digitale Pakete liegen im Median bei{" "}
            {euro(analyse.medianOhneDruck)} €. Der Drucker kostet also rund{" "}
            <strong className="font-semibold text-slate-900">
              {euro(analyse.aufschlag)} € mehr
            </strong>{" "}
            — für viele die Frage, ob sich das lohnt. Die ehrliche Antwort: Es
            hängt daran, was ihr mit den Bildern vorhabt. Wer ein
            Fotobox-Gästebuch führt oder will, dass die Gäste etwas mitnehmen,
            braucht den Druck. Für eine Party, bei der die Bilder abends in der
            Gruppe landen, reicht digital.
          </p>
        )}
      </Abschnitt>

      <Abschnitt
        id="flatrate-oder-kontingent"
        titel="Flatrate oder festes Kontingent?"
        untertitel="Die Antwort ist nicht die, die man erwartet."
      >
        <p>
          Es gibt zwei Modelle: {flatrate.anzahl} Pakete drucken unbegrenzt,{" "}
          {kontingent.anzahl} bringen eine feste Menge Papier mit.
        </p>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <th scope="col" className="py-2 pr-4 font-semibold">Modell</th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">Pakete</th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">ab</th>
                <th scope="col" className="py-2 text-right font-semibold">Median</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                  Druck-Flatrate
                </th>
                <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                  {flatrate.anzahl}
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                  {euro(flatrate.min)} €
                </td>
                <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                  {euro(flatrate.median)} €
                </td>
              </tr>
              <tr>
                <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                  Festes Kontingent
                </th>
                <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                  {kontingent.anzahl}
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                  {euro(kontingent.min)} €
                </td>
                <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                  {euro(kontingent.median)} €
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {flatrate.median < kontingent.median && (
          <p className="mt-4">
            <strong className="font-semibold text-slate-900">
              Die Flatrate ist im Median günstiger
            </strong>{" "}
            als ein Paket mit fester Menge — {euro(flatrate.median)} € gegenüber{" "}
            {euro(kontingent.median)} €. Das überrascht, hat aber einen Grund:
            Große Kontingente sind teuer, und wer viel drucken will, landet
            ohnehin im oberen Preisbereich. Wenn ihr also zwischen einem
            300-Bilder-Paket und einer Flatrate schwankt, lohnt der direkte
            Preisvergleich — oft ist die Flatrate die günstigere und dazu die
            sorgenfreie Wahl.
          </p>
        )}
        <p className="mt-4">
          Die Kontingente reichen von {kontingent.kleinstes} bis{" "}
          {kontingent.groesstes} Ausdrucken. Rechnet mit zwei bis drei Bildern
          pro Gast — bei einem Gästebuch mit dem Doppelten, weil dann jedes
          Motiv zweimal gedruckt wird. Bei 100 Gästen sind das 250 bis 500
          Bilder; Pakete unter 150 Ausdrucken sind damit nur für kleine Feiern
          gedacht.
        </p>
      </Abschnitt>

      {sekunden && (
        <Abschnitt
          id="druckzeit"
          titel="Wie lange ein Bild braucht"
          untertitel="Der Wert, den kaum jemand vergleicht — und der über die Schlange entscheidet."
        >
          <p>
            Im Median dauert ein Ausdruck{" "}
            <strong className="font-semibold text-slate-900">
              {sekunden.median} Sekunden
            </strong>
            . Die Spanne ist aber groß: Der schnellste Drucker im Vergleich
            braucht {sekunden.schnellste} Sekunden, der langsamste{" "}
            {sekunden.langsamste}.
          </p>
          {proStunde && (
            <p className="mt-4">
              Was das ausmacht, zeigt sich erst am Abend. Ein Durchgang sind
              meist drei bis vier Aufnahmen, danach wird gedruckt. Mit{" "}
              {sekunden.median} Sekunden je Bild schafft die Box rund{" "}
              <strong className="font-semibold text-slate-900">
                {proStunde} Gruppen pro Stunde
              </strong>{" "}
              — genug, solange nicht alle gleichzeitig wollen. Bei{" "}
              {sekunden.langsamste} Sekunden je Bild wird daraus eine Schlange,
              sobald sich nach dem Essen alle vor der Box sammeln.
            </p>
          )}
          <p className="mt-4">
            Der Druck selbst ist bei allen Geräten ein Thermosublimationsdruck:
            Das Bild ist trocken, wenn es herauskommt, und verwischt nicht. Es
            überlebt auch einen Abend in der Hosentasche — anders als ein
            Tintenstrahlausdruck.
          </p>
        </Abschnitt>
      )}

      <Abschnitt
        id="worauf-achten"
        titel="Worauf ihr beim Sofortdruck achten solltet"
        untertitel="Drei Dinge, die am Eventabend zählen."
      >
        <ol className="list-decimal space-y-3 pl-5 marker:font-semibold marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">
              Reicht das Papier?
            </strong>{" "}
            Rechnet vorher nach, statt zu hoffen. Ist es aufgebraucht, bekommt
            ihr mitten in der Feier keinen Nachschub — bei einer Versandbox
            schon gar nicht.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Passt das Layout?
            </strong>{" "}
            Die meisten Anbieter drucken auf Wunsch mit euren Namen, dem Datum
            oder dem Firmenlogo. Schickt die Vorlage einige Tage vorher — am
            Eventtag lässt sich daran nichts mehr ändern.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Wo steht die Box?
            </strong>{" "}
            Der Ausdruck kommt seitlich oder unten heraus. Steht die Box zu eng
            an der Wand, kommt niemand an die Bilder — und der Stapel wächst,
            bis ihn jemand bemerkt.
          </li>
        </ol>
        <p className="mt-6">
          Welche Boxen für eure Gästezahl genug drucken, zeigt der{" "}
          <Link
            href="/fotobox-vergleich"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Vergleich mit dem Filter nach Druckleistung
          </Link>
          .
        </p>
        <p className="mt-6 text-xs text-slate-500">
          Alle Zahlen aus dem Katalog, Stand {stand}.
        </p>
      </Abschnitt>
    </div>
  );
}
